import { LOAI_DINH_MUC } from "@/core/constants/const";
import {
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import type { IVatTuSelectedInKho } from "@/core/interfaces/Models";
import type { BbgtKhoItem } from "@/types/phieu-xuat-kho";
import { computed, provide, type Ref } from "vue";
import type {
    IPhieuXuatKhoSuppliesQuantityRow,
    PhieuXuatKhoLoaiPhieu,
    PhieuXuatKhoSuppliesSource,
    PhieuXuatKhoTableRow,
    PhieuXuatKhoVatTuTableMode,
} from "../types";
import { isThuHoiVatTu } from "./normalizeTableRow";
import { usePhieuXuatKhoPickFooter } from "./usePhieuXuatKhoPickFooter";
import {
    PHIEU_XUAT_KHO_TABLE_CELL_API_KEY,
    type PhieuXuatKhoTableCellApi,
} from "./usePhieuXuatKhoTableCellApi";
import { usePhieuXuatKhoTableColumns } from "./usePhieuXuatKhoTableColumns";
import { usePhieuXuatKhoTableFooter } from "./usePhieuXuatKhoTableFooter";
import { usePhieuXuatKhoTableQuantityHandlers } from "./usePhieuXuatKhoTableQuantityHandlers";

interface ProvideCellApiOptions {
  mode: Ref<PhieuXuatKhoVatTuTableMode | undefined>;
  source: Ref<PhieuXuatKhoSuppliesSource>;
  loaiPhieu: Ref<PhieuXuatKhoLoaiPhieu>;
  readonly: Ref<boolean>;
  isEditMode: Ref<boolean>;
  showThuHoiColumns: Ref<boolean>;
  showLyDoColumn: Ref<boolean>;
  showDeleteAction: Ref<boolean>;
  showPickFooter: Ref<boolean>;
  loaiDinhMuc: Ref<LOAI_DINH_MUC | undefined>;
  supplies: Ref<PhieuXuatKhoTableRow[]>;
  quantityRows: Ref<IPhieuXuatKhoSuppliesQuantityRow[]>;
  selectingKhoItem: Ref<BbgtKhoItem | null | undefined>;
  vatTuSelectedInKho: Ref<IVatTuSelectedInKho[]>;
  onQuantityChange: (rows: IPhieuXuatKhoSuppliesQuantityRow[]) => void;
  onDeleteItem: (idVatTu: number) => void;
}

export function useProvidePhieuXuatKhoTableCellApi(
  options: ProvideCellApiOptions
) {
  const isSummaryMode = computed(
    () => options.mode.value === "summary" || options.mode.value === "readonly"
  );

  const displayItems = computed(() => options.supplies.value);

  const columnContext = computed(() => ({
    mode: options.mode.value ?? "pick",
    source: options.source.value,
    readonly: options.readonly.value,
    showThuHoiColumns: options.showThuHoiColumns.value,
    showLyDoColumn: options.showLyDoColumn.value,
    showDeleteAction: options.showDeleteAction.value,
    showTonKhoColumn:
      options.source.value === "bbgt" || options.source.value === "manual",
    hasSupplies: displayItems.value.length > 0,
  }));

  const { footerIndexes, visibleColumns } =
    usePhieuXuatKhoTableColumns(columnContext);

  const {
    appendSummaryFooterRows,
    buildGroupTableData,
    footerRowClassName,
    footerSpanMethod,
  } = usePhieuXuatKhoTableFooter(footerIndexes);

  const quantityHandlers = usePhieuXuatKhoTableQuantityHandlers({
    source: options.source,
    readonly: options.readonly,
    isEditMode: options.isEditMode,
    selectingKhoItem: options.selectingKhoItem,
    quantityRows: options.quantityRows,
    vatTuSelectedInKho: options.vatTuSelectedInKho,
    loaiDinhMuc: options.loaiDinhMuc,
    onQuantityChange: options.onQuantityChange,
  });

  const {
    pickTableData,
    pickTotalAmount,
    pickTotalText,
    calcThanhTien,
    calcThanhTienTotal,
  } = usePhieuXuatKhoPickFooter({
    showPickFooter: options.showPickFooter,
    quantityRows: options.quantityRows,
    displayItems,
    appendSummaryFooterRows,
  });

  const getSupplyItemByVatTuId = (idVatTu: number) =>
    displayItems.value.find((row) => {
      const item = row as { id?: number; id_vat_tu?: number };
      return item.id === idVatTu || item.id_vat_tu === idVatTu;
    });

  const isVatTuThuHoi = (idVatTu: number): boolean => {
    const item = getSupplyItemByVatTuId(idVatTu) as
      | { thu_hoi?: unknown }
      | undefined;
    return isThuHoiVatTu(item?.thu_hoi);
  };

  const canEditThuHoiFields = (idVatTu: number): boolean => {
    if (options.readonly.value || isSummaryMode.value) {
      return false;
    }

    if (options.loaiPhieu.value === 2) {
      return false;
    }

    if (!quantityHandlers.getQuantityRowByVatTuId(idVatTu)) {
      return false;
    }

    const loaiPhieu = options.loaiPhieu.value;
    const isEditableLoaiPhieu = loaiPhieu === 0 || loaiPhieu === 1;

    return isEditableLoaiPhieu && isVatTuThuHoi(idVatTu);
  };

  const cellApi: PhieuXuatKhoTableCellApi = {
    get isSummaryMode() {
      return isSummaryMode.value;
    },
    get readonly() {
      return options.readonly.value;
    },
    get loaiPhieu() {
      return options.loaiPhieu.value;
    },
    get columnContext() {
      return columnContext.value;
    },
    get pickTotalAmount() {
      return pickTotalAmount.value;
    },
    get pickTotalText() {
      return pickTotalText.value;
    },
    formatQuantity,
    formatUnitPrice,
    formatAmount,
    calcThanhTien,
    calcThanhTienTotal,
    ...quantityHandlers,
    isVatTuThuHoi,
    canEditThuHoiFields,
    emitDeleteItem: options.onDeleteItem,
  };

  provide(PHIEU_XUAT_KHO_TABLE_CELL_API_KEY, cellApi);

  return {
    buildGroupTableData,
    footerRowClassName,
    footerSpanMethod,
    isSummaryMode,
    pickTableData,
    visibleColumns,
  };
}
